// The hub's calendar, in place of the browser's date input, which can't be
// styled: FancySelect's trigger and panel with a month inside. A value is a
// plain "YYYY-MM-DD", or "" for none, and every step works in calendar days, so
// nothing moves a day at a time zone.
import React from "react";
import { createPortal } from "react-dom";
import { usePanelPosition } from "./FancySelect";
import "./FancyDate.css";

const PANEL_WIDTH = 296;
const PANEL_HEIGHT = 352; // Six weeks' worth, so it opens upward rather than scrolling.
const VIEWPORT_PAD = 8;
const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const DAY_KEYS = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
const MONTH_KEYS = { PageUp: -1, PageDown: 1 };

const pad = (n) => String(n).padStart(2, "0");
const dayOf = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

function parseDay(value) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ""));
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
}

const worded = (value, options) => parseDay(value)?.toLocaleDateString("en-US", options) || "";

/** "Nov 3, 2026" */
export const formatDay = (value) => worded(value, { month: "short", day: "numeric", year: "numeric" });

function daysFrom(day, delta) {
  const d = parseDay(day);
  return dayOf(new Date(d.getFullYear(), d.getMonth(), d.getDate() + delta));
}

// The same day in another month, or that month's last when it's shorter.
function monthsFrom(day, delta) {
  const d = parseDay(day);
  const last = new Date(d.getFullYear(), d.getMonth() + delta + 1, 0).getDate();
  return dayOf(new Date(d.getFullYear(), d.getMonth() + delta, Math.min(d.getDate(), last)));
}

export default function FancyDate({
  value,
  onChange,
  placeholder = "Pick a date",
  disabled = false,
  ariaLabel,
  clearable = false,
}) {
  const [open, setOpen] = React.useState(false);
  // The day the keyboard is on. Its month is the one showing.
  const [cursor, setCursor] = React.useState(() => dayOf(new Date()));
  const wrapRef = React.useRef(null);
  const triggerRef = React.useRef(null);
  const panelRef = React.useRef(null);
  const focusCursor = React.useRef(false);
  const position = usePanelPosition(open, true, wrapRef, PANEL_HEIGHT);

  const chosen = parseDay(value) ? value : "";
  const today = dayOf(new Date());

  React.useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (!wrapRef.current?.contains(e.target) && !panelRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // The panel only exists once it has a position, so this waits for one.
  React.useEffect(() => {
    if (!position || !focusCursor.current) return;
    focusCursor.current = false;
    panelRef.current?.querySelector(`[data-day="${cursor}"]`)?.focus({ preventScroll: true });
  }, [position, cursor]);

  function openCalendar() {
    setCursor(chosen || today);
    focusCursor.current = true;
    setOpen(true);
  }

  function closeCalendar() {
    setOpen(false);
    triggerRef.current?.focus();
  }

  function pick(day) {
    closeCalendar();
    if (day !== chosen) onChange?.(day);
  }

  function onPanelKey(e) {
    if (e.key === "Escape") {
      e.preventDefault();
      closeCalendar();
      return;
    }
    if (!e.target.dataset?.day) return;
    const days = DAY_KEYS[e.key];
    const months = MONTH_KEYS[e.key];
    if (!days && !months) return;
    e.preventDefault();
    focusCursor.current = true;
    setCursor(days ? daysFrom(cursor, days) : monthsFrom(cursor, months));
  }

  const shown = parseDay(cursor);
  const year = shown.getFullYear();
  const month = shown.getMonth();
  const leadingBlanks = new Date(year, month, 1).getDay();
  const dayCount = new Date(year, month + 1, 0).getDate();
  const text = chosen ? formatDay(chosen) : placeholder;

  return (
    <div className={`select ${open ? "open" : ""}`} ref={wrapRef} style={{ position: "relative", width: "100%" }}>
      <button
        ref={triggerRef}
        type="button"
        className="selectTrigger"
        data-placeholder={chosen ? "false" : "true"}
        onClick={() => (open ? setOpen(false) : openCalendar())}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={ariaLabel ? `${ariaLabel}: ${text}` : undefined}
        disabled={disabled}
        style={{ width: "100%", opacity: disabled ? 0.6 : 1, fontSize: 16, minHeight: 44 }}
      >
        <span className="selectValue">{text}</span>
        <span className="selectCaret" aria-hidden />
      </button>

      {open && position
        ? createPortal(
            <div
              ref={panelRef}
              className="selectPanel fancyDate__panel"
              role="dialog"
              aria-label={ariaLabel || "Choose a date"}
              onKeyDown={onPanelKey}
              style={{
                position: "fixed",
                left: Math.max(VIEWPORT_PAD, Math.min(position.left, window.innerWidth - PANEL_WIDTH - VIEWPORT_PAD)),
                width: PANEL_WIDTH,
                top: position.top ?? "auto",
                bottom: position.bottom ?? "auto",
                maxHeight: position.maxHeight,
                overflow: "auto",
                zIndex: 9000,
                opacity: 1,
                visibility: "visible",
                transform: "translateY(0) scale(1)",
              }}
            >
              <div className="fancyDate__head">
                <button
                  type="button"
                  className="fancyDate__nav"
                  aria-label="Previous month"
                  onClick={() => setCursor(monthsFrom(cursor, -1))}
                >
                  {"‹"}
                </button>
                <div className="fancyDate__title" aria-live="polite">
                  {worded(cursor, { month: "long", year: "numeric" })}
                </div>
                <button
                  type="button"
                  className="fancyDate__nav"
                  aria-label="Next month"
                  onClick={() => setCursor(monthsFrom(cursor, 1))}
                >
                  {"›"}
                </button>
              </div>

              <div className="fancyDate__grid" data-month={cursor.slice(0, 7)}>
                {WEEKDAYS.map((name) => (
                  <div key={name} className="fancyDate__weekday" aria-hidden>
                    {name}
                  </div>
                ))}
                {Array.from({ length: leadingBlanks }, (_, i) => (
                  <span key={`blank-${i}`} />
                ))}
                {Array.from({ length: dayCount }, (_, i) => {
                  const day = dayOf(new Date(year, month, i + 1));
                  return (
                    <button
                      key={day}
                      type="button"
                      data-day={day}
                      tabIndex={day === cursor ? 0 : -1}
                      className={`fancyDate__day${day === chosen ? " selected" : ""}${day === today ? " today" : ""}`}
                      aria-label={worded(day, { month: "long", day: "numeric", year: "numeric" })}
                      aria-pressed={day === chosen}
                      aria-current={day === today ? "date" : undefined}
                      onClick={() => pick(day)}
                    >
                      {i + 1}
                    </button>
                  );
                })}
              </div>

              <div className="fancyDate__foot">
                <button type="button" className="fancyDate__link" onClick={() => pick(today)}>
                  Today
                </button>
                {clearable && chosen ? (
                  <button type="button" className="fancyDate__link" onClick={() => pick("")}>
                    Clear
                  </button>
                ) : null}
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
